import { useCallback, useEffect, useRef, useState } from 'react';
import { Plus, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { useUser } from '@/contexts/UserContext';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MigrarCifrado } from '@/components/MigrarCifrado';
import { AsistenteRenovar } from '@/components/certificados/AsistenteRenovar';
import { HistorialCertificados } from '@/components/certificados/HistorialCertificados';
import { TarjetaCertificadoActivo } from '@/components/certificados/TarjetaCertificadoActivo';
import { TarjetaCertificadoPreparado } from '@/components/certificados/TarjetaCertificadoPreparado';
import { TarjetaTareasTrasRenovacion } from '@/components/certificados/TarjetaTareasTrasRenovacion';
import { notificarCambioCertificados } from '@/hooks/useEstadoCertificados';
import { descargarPartePublica } from '@/utils/certificadosApi';
import type { DigitalCertificate } from '@/types';

export default function Certificados() {
  const { fetchWithAuth } = useUser();
  const [certificados, setCertificados] = useState<DigitalCertificate[]>([]);
  /** `null`: FACe no habilitado. */
  const [entornoFace, setEntornoFace] = useState<'pruebas' | 'produccion' | null>(null);
  const [faceNoCargada, setFaceNoCargada] = useState(false);
  const [errorLista, setErrorLista] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [asistenteAbierto, setAsistenteAbierto] = useState(false);
  const peticion = useRef(0);
  const listaCargada = useRef(false);

  const cargar = useCallback(async () => {
    const mia = ++peticion.current;
    // `Promise.resolve().then` convierte un lanzamiento síncrono de fetchWithAuth en un rechazo ya tratado abajo
    const lista = Promise.resolve().then(() => fetchWithAuth('/certificados')).then(
      async (r) => (r.ok ? { datos: (await r.json()) as DigitalCertificate[] } : null),
      () => null
    ).catch(() => null);
    // La configuración de FACe se parsea aparte: un fallo suyo no aborta la lista
    const face = Promise.resolve().then(() => fetchWithAuth('/face/config')).then(
      async (r) => {
        if (!r.ok) return 'fallo' as const;
        const c = await r.json();
        if (!c?.habilitado) return null;
        return c.entorno === 'produccion' ? ('produccion' as const) : ('pruebas' as const);
      },
      () => 'fallo' as const
    ).catch(() => 'fallo' as const);
    const [resultadoLista, resultadoFace] = await Promise.all([lista, face]);
    if (mia !== peticion.current) return; // llegó una carga más reciente

    if (resultadoLista) {
      listaCargada.current = true;
      setCertificados(resultadoLista.datos);
      setErrorLista(false);
    } else if (listaCargada.current) {
      toast.error('Error al cargar certificados');
    } else {
      setErrorLista(true);
    }
    setFaceNoCargada(resultadoFace === 'fallo');
    if (resultadoFace !== 'fallo') setEntornoFace(resultadoFace);
    setCargando(false);
  }, [fetchWithAuth]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  /** Tras cualquier operación: recarga la pantalla y avisa al aviso global y a los paneles. */
  const tras = async () => {
    await cargar();
    notificarCambioCertificados();
  };

  const descargar = async (certificado: DigitalCertificate) => {
    try {
      await descargarPartePublica(fetchWithAuth, certificado);
    } catch (error) {
      // Un fallo de red (TypeError) no trae texto del servidor: mensaje propio
      const delServidor = error instanceof Error && !(error instanceof TypeError);
      toast.error(delServidor ? (error as Error).message : 'No se pudo descargar el certificado.');
    }
  };

  if (cargando) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Shield className="h-12 w-12 mx-auto mb-4 animate-pulse" />
          <p className="text-muted-foreground">Cargando certificados...</p>
        </div>
      </div>
    );
  }

  const activo = certificados.find((c) => c.estado === 'activo');
  const preparados = certificados.filter((c) => c.estado === 'preparado');
  const archivados = certificados.filter((c) => c.estado === 'archivado');
  // Formato anterior: sin `estado` reconocido, pendientes de migrar
  const anteriores = certificados.filter((c) => !['activo', 'preparado', 'archivado'].includes(c.estado));

  const sinActivo = errorLista ? (
    <Alert>
      <AlertDescription className="space-y-3">
        <p>No se pudieron cargar tus certificados.</p>
        <Button onClick={cargar}>Reintentar</Button>
      </AlertDescription>
    </Alert>
  ) : anteriores.length > 0 ? (
    <Alert>
      <AlertDescription>
        Tienes certificados del formato anterior: migra el cifrado antes de subir uno nuevo.
      </AlertDescription>
    </Alert>
  ) : (
    <Card>
      <CardHeader>
        <CardTitle>No tienes ningún certificado activo</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {preparados.length > 0 ? (
          <p className="text-sm text-muted-foreground">
            Tienes un certificado preparado: compruébalo y actívalo más abajo.
          </p>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Sin un certificado activo no se pueden firmar facturas ni enviarlas a la AEAT o a FACe.
              Sube tu certificado en formato .p12 (FNMT, Camerfirma, ANF…).
            </p>
            <Button onClick={() => setAsistenteAbierto(true)}>
              <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
              Subir certificado
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="container mx-auto py-8 px-4 max-w-4xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Shield className="h-8 w-8" />
          Certificados Digitales
        </h1>
        <p className="text-muted-foreground mt-2">
          Gestiona el certificado con el que firmas facturas y te autenticas ante la AEAT y FACe
        </p>
      </div>

      {/* Temporal (SEC-04): migración del cifrado antiguo */}
      <MigrarCifrado onMigrado={tras} />

      {activo ? (
        <TarjetaCertificadoActivo
          certificado={activo}
          onRenovar={() => setAsistenteAbierto(true)}
          onDescargar={descargar}
        />
      ) : (
        sinActivo
      )}

      {anteriores.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Certificados del formato anterior</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <ul className="space-y-1">
              {anteriores.map((c) => (
                <li key={c.id}>
                  <span className="font-medium">{c.nombre}</span>
                  <span className="text-muted-foreground"> · caduca el {new Date(c.validUntil).toLocaleDateString('es-ES')}</span>
                </li>
              ))}
            </ul>
            <p className="text-muted-foreground">Pulsa «Migrar cifrado» para poder usarlos.</p>
          </CardContent>
        </Card>
      )}

      {faceNoCargada && preparados.length > 0 && (
        <Alert>
          <AlertDescription className="space-y-3">
            <p>No se pudo cargar tu configuración de FACe, necesaria para guiarte en la activación.</p>
            <Button onClick={cargar}>Reintentar</Button>
          </AlertDescription>
        </Alert>
      )}

      {!faceNoCargada && preparados.map((certificado) => (
        <TarjetaCertificadoPreparado
          key={certificado.id}
          certificado={certificado}
          entornoFace={entornoFace}
          onDescargar={descargar}
          onCambio={tras}
        />
      ))}

      <TarjetaTareasTrasRenovacion archivados={archivados} onCambio={tras} />
      <HistorialCertificados archivados={archivados} onCambio={tras} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">¿Para qué sirve la firma electrónica?</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground space-y-2">
            <p>• <strong>Obligatorio</strong> para facturas a Administraciones Públicas (FACe)</p>
            <p>• Garantiza la <strong>autenticidad</strong> e <strong>integridad</strong> de la factura</p>
            <p>• Cumple con el formato <strong>Facturae 3.2.2</strong> y firma <strong>XAdES-BES</strong></p>
            <p>• Válido legalmente según la <strong>Ley 25/2013</strong></p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Seguridad de tus certificados</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground space-y-2">
            <p>• Cifrado <strong>AES-256-GCM</strong> con una clave del servidor, nunca guardada junto a los datos</p>
            <p>• Claves privadas <strong>nunca</strong> se envían al navegador</p>
            <p>• Firma realizada de forma <strong>segura</strong> en el servidor</p>
            <p>• Solo tú puedes acceder a tus certificados</p>
          </CardContent>
        </Card>
      </div>

      <Alert>
        <Shield className="h-4 w-4" aria-hidden="true" />
        <AlertDescription>
          Al renovar, el certificado nuevo queda preparado y solo pasa a ser el activo cuando FACe lo reconoce:
          los envíos no se cortan.
        </AlertDescription>
      </Alert>

      <AsistenteRenovar
        abierto={asistenteAbierto}
        onCerrar={() => setAsistenteAbierto(false)}
        onSubido={() => {
          toast.success('Certificado subido.');
          tras();
        }}
      />
    </div>
  );
}
